import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, CircularProgress, Alert, List, 
  ListItem, ListItemText, Chip, Divider as MuiDivider, 
  FormControl, InputLabel, Select, MenuItem, OutlinedInput
} from '@mui/material';
import { CheckCircle as CheckIcon, EventBusy, EventNote } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
// Importamos el nuevo hook
import { useCitasPaciente } from '../components/useCitasPaciente';

const TAB_NAMES = {
  VAC: "Vacunas",
  DES: "Desparasitación",
  PIP: "Pipetas",
  CIR: "Cirugías",
  TER: "Terapias",
  BAN: "Baños",
  CON: "Consulta" 
};

const SERVICIOS_DISPONIBLES = [
  'Baño', 'Consulta', 'Emergencia', 'Vacunas', 'Pipeta', 'Terapia', 
  'Desparasitación', 'Hospedaje', 'Primera Visitia', 'sin cita'
];

const ConfirmacionTurno = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { qrCode, nombrePaciente: initialNombre, esManual = false } = location.state || {};

  const [nombrePaciente, setNombrePaciente] = useState(initialNombre || '');
  const [idPaciente, setIdPaciente] = useState(null);
  const [raza, setRaza] = useState(null);
  const [especie, setEspecie] = useState(null);
  
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [error, setError] = useState('');
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [serviciosSeleccionados, setServiciosSeleccionados] = useState([]);

  // --- USO DEL NUEVO HOOK ---
  const { vencidas, futuras, loading: loadingCitas } = useCitasPaciente(idPaciente);

  // Efecto para obtener info básica del paciente vía QR
  useEffect(() => {
    if (!esManual && qrCode) {
      const fetchPatientInfo = async () => {
        setLoadingInfo(true);
        // Se restituye el prefijo /pacientes requerido por el enrutador de FastAPI
        const urlConsultada = `/pacientes/cola/info-qr/${qrCode}`;
        
        console.log(`%c[GET] Iniciando petición a: ${urlConsultada}`, 'color: #00bcd4; font-weight: bold;');
        
        try {
          const res = await api.get(urlConsultada);
          
          console.log('%c[GET RESPONSE] Datos recibidos del QR:', 'color: #4caf50; font-weight: bold;', res.data);
          
          const data = res.data;
          setNombrePaciente(data.nombre_paciente || '');
          setIdPaciente(data.id_paciente);
          setRaza(data.raza);
          setEspecie(data.especie);
        } catch (err) {
          console.error('%c[GET ERROR] Falló la carga de info por QR:', 'color: #f44336; font-weight: bold;', {
            mensaje: err.message,
            codigo: err.code,
            status: err.response?.status,
            statusText: err.response?.statusText,
            responseData: err.response?.data,
            config: err.config
          });
          setError("No se pudo cargar la información del paciente.");
        } finally {
          setLoadingInfo(false);
        }
      };
      fetchPatientInfo();
    }
  }, [esManual, qrCode]);

  const handleConfirmar = async () => {
    if (serviciosSeleccionados.length === 0) {
      setError("Debe seleccionar al menos un servicio.");
      return;
    }
    setLoadingConfirm(true);
    setError('');

    // Estructura base del payload
    const payload = { 
      qr_code: qrCode || 'SIN-QR', 
      tipo_servicio: serviciosSeleccionados 
    };
    
    let endpoint = '';
    let finalPayload = { ...payload };

    // Se restituye el prefijo /pacientes para coincidir con el backend
    if (esManual) {
      endpoint = '/pacientes/cola/sin-qr';
      finalPayload = { ...payload, nombre: nombrePaciente };
    } else {
      endpoint = '/pacientes/cola/agregar';
    }

    console.log(`%c[POST] Enviando formulario a: ${endpoint}`, 'color: #ff9800; font-weight: bold;');
    console.log('%c[POST PAYLOAD] Datos enviados:', 'color: #ff9800;', finalPayload);

    try {
      const res = await api.post(endpoint, finalPayload);
      
      console.log('%c[POST RESPONSE] Servidor respondió con éxito:', 'color: #4caf50; font-weight: bold;', res.data);

      navigate('/exito-turno-asignado', {
        replace: true,
        state: {
          qrCode: qrCode || 'SIN-QR',
          nombrePaciente: res.data.nombre_paciente || nombrePaciente,
          numeroTurno: res.data.numero_turno,
          raza: raza || res.data.raza || null,
          especie: especie || res.data.especie || null,
          servicios: serviciosSeleccionados
        },
      });
    } catch (err) {
      console.error('%c[POST ERROR] Falló el registro del turno:', 'color: #f44336; font-weight: bold;', {
        mensaje: err.message,
        codigo: err.code,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        config: err.config
      });
      setError(err.response?.data?.detail || 'Error al asignar turno');
    } finally {
      setLoadingConfirm(false);
    }
  };

  const RenderCitaList = ({ citas = [], titulo, color, icon }) => {
    if (!citas || citas.length === 0) return null;
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" color={color} fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {icon} {titulo} ({citas.length})
        </Typography>
        <Paper variant="outlined" sx={{ bgcolor: color === 'error.main' ? '#fff5f5' : '#f0f7ff', borderRadius: 2, borderColor: color === 'error.main' ? '#feb2b2' : '#bee3f8', overflow: 'hidden' }}>
          <List dense>
            {citas.map((cita, i) => (
              <React.Fragment key={cita.id_cita || i}>
                {i > 0 && <MuiDivider />}
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={TAB_NAMES[cita.tab] || cita.tab} size="small" color={color === 'error.main' ? "error" : "primary"} variant="outlined" />
                        <Typography variant="body2" fontWeight="bold">{cita.descripcion || cita.razon_consulta || 'Sin detalle'}</Typography>
                      </Box>
                    }
                    secondary={`Fecha: ${cita.fecha_cita ? new Date(cita.fecha_cita).toLocaleDateString('es-SV') : 'N/A'} | Razón: ${cita.razon_consulta || 'N/A'}`}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Paper elevation={4} sx={{ p: 4, maxWidth: 550, textAlign: 'center', borderRadius: 3, width: '100%' }}>
        <CheckIcon sx={{ fontSize: 80, color: '#10b981', mb: 2 }} />
        <Typography variant="h5" fontWeight="bold">Confirmar Turno</Typography>

        <Box sx={{ my: 3, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          {loadingInfo ? <CircularProgress size={24} /> : (
            <>
              <Typography variant="h4" fontWeight="bold" color="primary">{nombrePaciente || 'Cargando...'}</Typography>
              <Typography variant="body2" color="text.secondary">{especie || '—'} • {raza || '—'}</Typography>
            </>
          )}
        </Box>

        <Box sx={{ mb: 3, textAlign: 'left' }}>
          <FormControl fullWidth>
            <InputLabel id="servicios-label">¿Qué servicios requiere?</InputLabel>
            <Select
              labelId="servicios-label"
              multiple
              value={serviciosSeleccionados}
              onChange={(e) => setServiciosSeleccionados(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="¿Qué servicios requiere?" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" color="secondary" />
                  ))}
                </Box>
              )}
            >
              {SERVICIOS_DISPONIBLES.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loadingCitas ? (
          <Box sx={{ my: 2 }}><CircularProgress size={24} /><Typography variant="caption" display="block">Buscando citas...</Typography></Box>
        ) : (
          <Box sx={{ textAlign: 'left', maxHeight: 300, overflowY: 'auto', px: 1, border: '1px solid #edf2f7', borderRadius: 2, mb: 2 }}>
            <RenderCitaList citas={vencidas} titulo="CITAS VENCIDAS" color="error.main" icon={<EventBusy fontSize="small" />} />
            <RenderCitaList citas={futuras} titulo="CITAS PARA HOY / PRÓXIMAS" color="primary.main" icon={<EventNote fontSize="small" />} />
            {(vencidas.length === 0 && futuras.length === 0) && !loadingInfo && !loadingCitas && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>El paciente no tiene citas pendientes.</Alert>
            )}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          <Button variant="outlined" fullWidth onClick={() => navigate(-1)} disabled={loadingConfirm}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={handleConfirmar}
            disabled={loadingConfirm || loadingCitas || loadingInfo}
          >
            {loadingConfirm ? 'Procesando...' : 'Confirmar'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ConfirmacionTurno;